import React, { Fragment, useEffect, useRef, useState } from 'react';
import LoadingIndicator from '../LoadingIndicator';
import {
  IContent,
  IQuery,
  IQueryContent,
  RESPONSE_BY,
  RESPONSE_TYPE
} from '../../../models/IQueryType';
import { MotifChip, MotifToast } from '@ey-xd/motif-react';

import { convertNumber } from '../../../utils/numberConverter';
import { useDispatch, useSelector } from 'react-redux';
import {
  getResponseSourceType,
  sanitizeHeaderText,
  openLinkInSameTab
} from '../../../utils/commonUtils';
import LABELS from '../../../ui-constants/en.json';
import PageConfig from '../../../ui-constants/en.json';
import './ChatReply.scss';
import { deepResearchCopyIcon, deepResearchDownload, deepResearchRegenerateIcon, deepResearchSuccessIcon, regenerateImg } from '../../../commonImage';
import { IStore, store } from '../../../store';
import TableContent from './TableContent';
import ConfirmationSourceModal from './ConfirmationSourceModal';
import { exportToCSV } from '../utils/exportToCSV';
import { publicSources } from '../../../label.constant';
import suggestionIcon from '../../../assets/images/suggestion.png';
import OPENAI_IMG_ICON from '../../../assets/images/AI_EYP.png';
import EngagementTable from './EngagementTable';

import { useNavigate } from 'react-router-dom';
import {
  clearChatHistory,
  setLastSavedThreadID,
  setSelectedChatThread
} from '../../../store/actions/userChatActions';
import { triggerDnfOpenAiSearch } from '../../../store/actions/commonActions';
import DynamicTextRenderer from './ChatSteamResponseFormatting';
import TableMarketReport from './TableMarketReport';
import { Guid } from 'guid-typescript';
import { handleCopy } from '../../../utils/Copychat';
import TableTranscripts from './TableTranscripts';

type GetChatResponseProps = {
  chatData?: IQuery;
  loader?: boolean;
  isNew?: boolean;
  content?: IContent[];
  isSE: boolean;
  source?: string;
  messageLoading?: boolean;
  chunkLoading?: boolean;
  userQueryWithResponse?: any;
  isLastItem?: boolean;
  isEngagement?: boolean;
  scrollToBottom?: () => void;
  handleRegenerate?: (ev: any, query: string) => void;
  handleSendMessage?: (ev: any, query: string) => void;
  isCompletelyAbortedResposne?: boolean;
  isParitallyAbortedResponse?: boolean;
  message?: string;
  showDnfSuggestion?: boolean;
  setShowDnfSuggestionState?: (value : boolean) => void;
  isMarketReports?: boolean;
  isCombined ?:boolean;
  isTranscripts?: boolean;
};

type OptionsButtonProps = {
  options: { onClick: any; label: string }[];
};
interface RootObject {
  Project_End_Date: string;
  Project_type: string;
  Geography: string[];
  Authors: Author[];
}
interface Author {
  author_name: string;
  author_email: string;
  role: string;
}
export type ParsedTableData = { headers: string[]; values: string[][] };

export const errorMessage = {
  SEError:
    'Oops! This data is not covered by Embryonic. Please refer to secondary research for more information on this.',
  OpenAIError: 'Oops! Something went wrong. Please try again after sometime.'
};

export const getSourcesList = (replyData: any, replySources: any) => {
  let result = replyData.split('\n');
  const uniqueSrc = [...replySources, ...result];
  return uniqueSrc.filter((string) => Boolean(string));
};

export const getSourcesDetails = (replySources: Array<any>) => {
  let result = [];
  if (
    replySources &&
    (replySources[0] === 'Embryonic' ||
      replySources[0] === 'Open AI' ||
      replySources[0] === 'Market Reports' ||
      replySources[0] === 'Discover: Sector Insights' ||
      replySources[0] === 'Discover Knowledge Capture' ||
      replySources[0] === 'Surveys' ||
      replySources[0] === 'URL' ||
      replySources[0] === 'EY-P Strategy projects')
  ) {
    result = replySources.slice(1);
  } else {
    result = replySources;
  }
  const uniqueSrc = [...result];
  return uniqueSrc.filter((string) => Boolean(string));
};

export const prepareContentForTable = (content: IContent[]) => {
  const tableData: ParsedTableData = { headers: [], values: [] };
  content.forEach((el) => {
    if (el && typeof el === 'object') {
      let valuesSegment: any[] = [];
      Object.entries(el).forEach(([key, value]) => {
        if (!tableData.headers.includes(key)) {
          tableData.headers.push(key);
        }
        if (
          key.toLowerCase().includes('year') ||
          key.toLowerCase().includes('id') ||
          key.toLowerCase().includes('quarter') ||
          key.toLowerCase().includes('count') ||
          key.toLowerCase().includes('date') ||
          key.toLowerCase().includes('Code') ||
          key.toLocaleLowerCase().includes('portfolio')
        ) {
          valuesSegment.push({ val: value });
        } else {
          valuesSegment.push(convertNumber(value, 1));
        }
      });
      tableData.values.push(valuesSegment);
    }
  });
  return tableData;
};
const ContentTable = ({
  tableContent,
  tableLength
}: {
  tableContent?: ParsedTableData;
  tableLength?: number;
}) => {
  return (
    <div className='table-wrapper'>
      <table className='content-table'>
        <tbody className='content-table-body'>
          <tr>
            {tableContent?.headers.map((header: string) => (
              <th key={`table-header-${header}`} className='table-border-bottom'>{sanitizeHeaderText(header)}</th>
            ))}
          </tr>
          {tableContent?.values.map((valArr: string[], i) => (
            <tr key={`table-row-${i}`}>
              {valArr.map((val: any, i) => (
                <td key={`table-row-value-${i}`} className='table-border-bottom'>
                  <span title={val.title ? val.title : null}>{val.val}</span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const GetChatResponse = ({
  chatData,
  loader,
  isNew,
  isSE,
  messageLoading,
  chunkLoading = false,
  userQueryWithResponse,
  isLastItem = false,
  isEngagement = false,
  scrollToBottom,
  handleRegenerate = () => {},
  handleSendMessage = () => {},
  message,
  isCompletelyAbortedResposne = false,
  isParitallyAbortedResponse = false,
  showDnfSuggestion = false,
  setShowDnfSuggestionState = () => {},
  isMarketReports = false,
  isCombined = false,
  isTranscripts = false
}: GetChatResponseProps) => {
  const dispatch = useDispatch();
  // const { exportToCSV, exportToPPT } = useExportToFile();
  const [collapseHiddenLinks, setCollapseHiddenLinks] = React.useState(true);
  const [collapseHiddenText, setCollapseHiddenText] = React.useState(true);
  const [replyData, setReplyData] = React.useState<IQueryContent | null>();
  const [tableContent, setTableContent] = React.useState<ParsedTableData>();
  const [visibleLinks, setVisibleLinks] = React.useState<string[]>([]);
  const [visibleTextList, setvisibleTextList] = React.useState<string[]>([]);
  const [hiddenLinks, setHiddenLinks] = React.useState<string[]>([]);
  const [hiddenTextList, setHiddenTextList] = React.useState<string[]>([]);
  const [showOptions, setShowOptions] = React.useState(false);
  const [tokenSqlErrorMessage, setTokenSqlErrorMessage] = React.useState('');
  const [hasSourceLink, setHasSourceLink] = React.useState(true);
  const [showTable, SetShowTable] = React.useState<boolean>(true);
  const [showUploadInitiated, setShowUploadInitiated] = useState({
    action: '',
    flag: false
  });
  const [csvDownloadError, setCsvDownloadError] = useState(false);
  const [showCopyText, setShowCopyText] = useState(false);
  const [showCopyFailure, setShowCopyFailure] = useState(false);
  const [someResponseId, setSomeResponseId] = useState('');
  const {
    isRightSideBarOpen,
    anySourceSelected,
    eyIPInterRadio = '',
    validURLinputValue,
    checkedCheckboxes = []
  } = useSelector((state: IStore) => state.common);
  const userData = useSelector((state: IStore) => state.userProfile?.userData);
  const selectedDataSourceSection = useSelector(
    (state: any) => state.userChat.selectedDataSourceSection
  );
  const [sourceType, setSourceType] = React.useState('');
  const isSideBarOpen = false;
  const pathName = location.pathname;
  const navigate = useNavigate();
  const chatbotImageClass = isNew ? 'chatreply__img-container' : 'chatreply__img-container-dark';
  const companyProfileDownloadSuggestion = 'Would you like to download the company profile?';
  const sectorTrendDownloadSuggestion =
    'Would you like to download the sector trend analysis report?';


  React.useEffect(() => {
    let data: IQueryContent | null = chatData
      ? chatData[RESPONSE_BY.STRATEGY_EDGE] || chatData[RESPONSE_BY.OPENAI]
      : null;
    if (data && !data.Sources) {
      data.Sources = [eyIPInterRadio];
    }
    (data?.summary &&
      data?.summary.length &&
      eyIPInterRadio === LABELS.SECTOR_INSIGHTS_TILE &&
      data?.summary?.toLowerCase().includes('no result found')) ||
    data?.summary?.toLowerCase().includes('no results found')
      ? SetShowTable(false)
      : SetShowTable(true);
    if (data && isLastItem) data.isChunkLoading = chunkLoading;
    setReplyData(data);
    setSomeResponseId(`copy-${Guid.create()}`);
  }, [chatData]);

  React.useEffect(() => {
    if (replyData && isLastItem) replyData.isChunkLoading = chunkLoading;
    if (replyData?.Content) {
      if (replyData?.Content.length > 10) {
        setTableContent(prepareContentForTable(replyData?.Content.slice(0, 10)));
      } else {
        setTableContent(prepareContentForTable(replyData?.Content));
      }
    }

    if (replyData?.Sources && replyData.Sources.length > 0) {
      if (typeof replyData?.Sources === 'string') {
        replyData.Sources = [replyData?.Sources];
      }
      const sources = getSourcesDetails(replyData?.Sources || []);
      if (sources.length > 0) {
        if (sourceType === 'Surveys') {
          setvisibleTextList(sources && sources[0]?.split(',').slice(0, 2));
          setHiddenTextList(sources && sources[0]?.split(',').slice(2));
        } else {
          setVisibleLinks(sources.slice(0, 5));
          setHiddenLinks(sources.slice(5));
        }
        sources?.map((links) => {
          if (links.includes('://') || links.includes('www.')) {
            setHasSourceLink(true);
          } else {
            setHasSourceLink(false);
          }
        });
      }
      setReplyData({ ...replyData, summary: replyData?.summary?.replace(/Summary:|\*\*Summary\*\*:|:|SummaryResponse:/gi, '') });
    }

    if (checkedCheckboxes && checkedCheckboxes.length > 0) {
      const [embryonic, openAI, marketReports] = publicSources;
      const hasEmbryonic = checkedCheckboxes.includes(embryonic);
      const hasOpenAI = checkedCheckboxes.includes(openAI);
      const hasMarket = checkedCheckboxes.includes(marketReports);

      switch (checkedCheckboxes.length) {
        case 1:
          if (hasMarket) setSourceType(marketReports);
          else if (hasEmbryonic) setSourceType(embryonic);
          else if (hasOpenAI) setSourceType(openAI);
          break;
        case 2:
          if (hasMarket && hasEmbryonic) setSourceType('WithEmbMR');
          else if (hasMarket && hasOpenAI) setSourceType('WithOAIMR');
          else if (hasEmbryonic && hasOpenAI) setSourceType('Combined');
          break;
        case 3:
          if (hasMarket && hasEmbryonic && hasOpenAI) setSourceType('WithMR');
          break;
        default:
          break;
      }
    }

    if (eyIPInterRadio) {
      setSourceType(eyIPInterRadio);
    }
    if (validURLinputValue) {
      setSourceType('Upload URL');
    }
    if (selectedDataSourceSection === 5 && checkedCheckboxes.length === 0 && !eyIPInterRadio) {
      setSourceType('Experts');
    }
  }, [replyData?.summary, replyData?.Sources]);

  const getInfoSources = React.useCallback(
    (replyData: any, replySources: any) => {
      return getSourcesList(replyData, replySources);
    },
    [replyData?.summary]
  );

  const formatLink = (link: string) => {
    const sourceName = link?.split('-')[0];
    return sourceName ? sourceName : link;
  };
  const totalSlide: any = {
    Content: [
      {
        file_name: '',
        page_number: []
      }
    ]
  };

  useEffect(() => {
    if (
      (replyData?.summary && replyData?.summary?.includes(LABELS.TOKEN_ERR)) ||
      replyData?.summary?.includes('SQL_ERROR') ||
      replyData?.summary?.includes('https://aka.ms/oai/quotaincrease')
    ) {
      setTokenSqlErrorMessage(LABELS.chat_reply.SE_TOKEN_ERROR_MSG);
    } else {
      setTokenSqlErrorMessage('');
    }
  }, [replyData?.summary]);

  useEffect(() => {
    scrollToBottom?.();
  }, [replyData?.showTable]);

  const openDnfQuestionInNewChatHistory = () => {
    setShowDnfSuggestionState(false);
    dispatch(triggerDnfOpenAiSearch(true));
    navigate(pathName + '/');
    dispatch(clearChatHistory());
    dispatch(setLastSavedThreadID(0));
    dispatch(setSelectedChatThread({}));
  };

  const handleExportToExcel = async () => {
    setCsvDownloadError(false);
    setShowUploadInitiated({ action: '', flag: true });
    try {
      await exportToCSV(
        replyData?.sqlQuery,
        RESPONSE_BY.EMBROYNIC,
        userData,
        checkedCheckboxes,
        eyIPInterRadio,
        validURLinputValue
      );
      setShowUploadInitiated({ action: '', flag: false });
    } catch (error) {
      setCsvDownloadError(true);
      setTimeout(() => {
        setShowUploadInitiated({ action: '', flag: false });
      }, 3500);
    }
  };

  const convertEngagementReleventFilesToArray = () => {
    const relevantFiles = replyData?.relevantFiles;
    const tableArray: any = {
      headers: [
        sanitizeHeaderText('Name(s)'),
        sanitizeHeaderText('Role(s)'),
        sanitizeHeaderText('Project type'),
        sanitizeHeaderText('Project end date'),
        sanitizeHeaderText('Geographic focus of work')
      ],
      values: []
    };
    let referenceDocumentsContentArray: any = [];
    relevantFiles?.map((file: any) => {
      let authorsText = '';
      file?.Authors.forEach((author: any) => {
        authorsText +=
          author.author_name + (author.author_email && '(' + author.author_email + '), ');
      });
      if (authorsText[authorsText.length - 1] === ' ') authorsText = authorsText.slice(0, -2);
      //authorsText = wrapText(authorsText, 100);
      referenceDocumentsContentArray.push({ val: authorsText });
      referenceDocumentsContentArray.push({ val: 'Role 1, Role 2' });
      referenceDocumentsContentArray.push({ val: 'Pro Type' });
      referenceDocumentsContentArray.push({ val: 'Pro End dtae' });
      referenceDocumentsContentArray.push({ val: 'Geographic' });
      tableArray.values.push(referenceDocumentsContentArray);
    });
    return tableArray;
  };

  const convertReleventFilesToArray = () => {
    const relevantFiles = replyData?.relevantFiles;
    const tableArray: any = {
      headers: isMarketReports
        ? [
            sanitizeHeaderText('Document Title'),
            sanitizeHeaderText('Page nos.'),
            sanitizeHeaderText('Source')
          ]
        : [sanitizeHeaderText('Relevant Document(s)'), sanitizeHeaderText('Authors')],
      values: []
    };
    relevantFiles?.map((file: any) => {
      let referenceDocumentsContentArray: any = [];
      let authorsText = '';
      let pageNo = '';
      referenceDocumentsContentArray.push({ val: file.file_name });
      if (!isMarketReports) {
        file?.authors?.forEach((author: any) => {
          authorsText +=
            author.author_name + (author.author_email && '(' + author.author_email + '), ');
        });

        if (authorsText[authorsText.length - 1] === ' ') authorsText = authorsText.slice(0, -2);
        referenceDocumentsContentArray.push({ val: authorsText });
      } else {
        file.page_number.length &&
          file.page_number.forEach((numer: any, i: number) => {
            pageNo = pageNo + numer + (i === file.page_number.length - 1 ? '' : ', ');
          });
        referenceDocumentsContentArray.push({ val: pageNo });
        referenceDocumentsContentArray.push({ val: file?.source });
      }
      tableArray.values.push(referenceDocumentsContentArray);
    });
    return tableArray;
  };

  useEffect(() => {
    if (showCopyText) {
      setTimeout(() => {
        setShowCopyText(false);
      }, 3000);
    }
    if (showCopyFailure) {
      setTimeout(() => {
        setShowCopyFailure(false);
      }, 3000);
    }
  }, [showCopyFailure, showCopyText]);

  const handleCopySnapshot = async () => {
    let r = document.createRange();
    r.selectNode(document.getElementsByClassName(`resCopyEmb${someResponseId}`)[0]);
    window.getSelection()?.removeAllRanges();
    window.getSelection()?.addRange(r);
    document?.execCommand('copy');
    window.getSelection()?.removeAllRanges();

    setShowCopyText(true);
    setTimeout(() => {
      setShowCopyText(false);
    }, 1000);
  };

  const selectedSourceName = eyIPInterRadio || checkedCheckboxes[0];

const handleCopyChat = () => {
  const mainSource = selectedSourceName;
  const isEmbryonic = checkedCheckboxes[0] === 'Embryonic';
  const isOpenAI = checkedCheckboxes[0] === 'Open AI';
  const isEmbAndOpenAI =
    checkedCheckboxes.length > 1 &&
    checkedCheckboxes[0] === 'Embryonic' &&
    checkedCheckboxes[1] === 'Open AI';
  const hasMarketReports =
    checkedCheckboxes.length > 1 && checkedCheckboxes.includes('Market reports');

  if (isCombined && hasMarketReports) {
    handleCopy(replyData, setShowCopyText, someResponseId, 'Market reports', isCombined);
    return;
  }

  if (
    mainSource === 'Discover: Sector Insights' ||
    mainSource === 'EY-P Strategy projects' ||
    mainSource === 'Market reports' ||
    hasMarketReports
  ) {
    handleCopy(replyData, setShowCopyText, someResponseId, 'Market reports');
    return;
  }

  if (selectedDataSourceSection === 5) {
    handleCopy(replyData, setShowCopyText, someResponseId, 'Experts');
    return;
  }

  if (isEmbAndOpenAI || isEmbryonic || isOpenAI || mainSource === 'Surveys' || mainSource === 'Transcripts') {
    handleCopySnapshot();
  }
};

  return (
    <Fragment>
      <div className={!isSideBarOpen ? 'chatreply' : 'chatreply sidebar-open'}>
        {!loader && (
          <div
            className={
              !isSideBarOpen ? `chatreply__resp-box ` : 'chatreply__resp-box sidebar-open'
            }>
            <div className={'chatreply__resp-box__resp'}>
              <div className={`resCopyEmb${someResponseId}`}>
                <span
                  className={`resCopy${someResponseId} ${isCompletelyAbortedResposne ? 'stop-generatio-label' : ''}`}>
                  <DynamicTextRenderer
                    text={
                      replyData?.ResponseType !== RESPONSE_TYPE.ERROR && replyData?.summary
                        ? chatData?.[RESPONSE_BY.OPENAI] &&
                          replyData.summary.startsWith(PageConfig.SORRY_ERR) &&
                          !replyData.summary.startsWith(PageConfig.SORRY_ERR1)
                          ? PageConfig.GENERIC_OOPS_ERROR
                          : chatData?.[RESPONSE_BY.STRATEGY_EDGE] &&
                              replyData.summary.startsWith(PageConfig.SORRY_ERR) &&
                              !replyData.summary.startsWith(PageConfig.SORRY_ERR1)
                            ? validURLinputValue
                              ? PageConfig.chat_reply.UPLOAD_URL_ERROR
                              : PageConfig.chat_reply.EMBROYNIC_ERROR
                            : replyData.summary.trim()
                        : ''
                    }
                    isMarketReports={isMarketReports}
                  />
                  {chunkLoading && isLastItem && (
                    <span>
                      <LoadingIndicator
                        color='#2E2E38'
                        parentWidth='8px'
                        zeroPercentheight='28px'
                        fifltyPercentheight='10px'
                        indicatorHeight={'19px'}
                      />
                    </span>
                  )}
                  {replyData?.ResponseType === RESPONSE_TYPE.ERROR &&
                    !validURLinputValue &&
                    chatData?.[RESPONSE_BY.STRATEGY_EDGE] &&
                    !tokenSqlErrorMessage &&
                    PageConfig.chat_reply.EMBROYNIC_ERROR}
                  {replyData?.ResponseType === RESPONSE_TYPE.ERROR &&
                    chatData?.[RESPONSE_BY.STRATEGY_EDGE] &&
                    validURLinputValue &&
                    !tokenSqlErrorMessage &&
                    PageConfig.chat_reply.UPLOAD_URL_ERROR}
                  {replyData?.ResponseType === RESPONSE_TYPE.ERROR &&
                    chatData?.[RESPONSE_BY.STRATEGY_EDGE] &&
                    tokenSqlErrorMessage.length > 0 &&
                    tokenSqlErrorMessage}
                  {replyData?.ResponseType === RESPONSE_TYPE.ERROR &&
                    chatData?.[RESPONSE_BY.OPENAI] &&
                    LABELS.chat_reply.OPENAI_ERROR_MSG}
                </span>
                <div>
                  {!isEngagement && !isTranscripts &&
                    replyData?.showTable &&
                    replyData?.ResponseType === RESPONSE_TYPE.TABLE && (
                      <>
                        <ContentTable
                          tableContent={tableContent}
                          tableLength={replyData?.Content?.length}
                        />
                      </>
                    )}
                  {!isMarketReports && !isTranscripts &&
                    !isEngagement &&
                    replyData?.showTable &&
                    showTable &&
                    (replyData?.relevantFiles?.length ?? 0) > 0 && (
                      <>
                        <TableContent tableContent={replyData?.relevantFiles || []} />
                      </>
                    )}

                  {replyData?.showTable &&
                    showTable &&
                    (replyData?.relevantFiles?.length ?? 0) > 0 &&
                    isEngagement && (
                      <div className='chatreply__engagement-container'>
                        <EngagementTable tableContent={replyData?.relevantFiles ?? []} />
                      </div>
                    )}
                </div>
              </div>
            </div>
            {!chunkLoading && (eyIPInterRadio?.length > 0 || selectedDataSourceSection == 5) && (
              <Fragment>
                <div className='disclaimer'></div>
              </Fragment>
            )}
            {replyData?.Sources && (
              <Fragment>
                {!isCompletelyAbortedResposne && (
                  <div
                    className={
                      isSideBarOpen
                        ? 'chatreply__resp-box__footer slider'
                        : 'chatreply__resp-box__footer'
                    }>
                    {isParitallyAbortedResponse && (
                      <div className='chatreply__resp-box__footer__source-box'>
                        <p className='stop-generatio-label'>
                          {LABELS.chat_reply.PARTIAL_STOP_GENERATION_MSG}
                        </p>
                      </div>
                    )}
                    {sourceType !== 'Surveys' && (
                      <div className='chatreply__resp-box__footer__source-box'>
                        <p>
                          Source:{' '}
                          {getResponseSourceType(sourceType, validURLinputValue) ===
                          'Expert Call Transcripts (DBS)' ? (
                            <>
                              Expert Call Transcripts <sup className='chatreply__sup-text'>{'(DBS)'}</sup>
                            </>
                          ) : (
                            getResponseSourceType(sourceType, validURLinputValue)
                          )}
                        </p>
                        {}
                      </div>
                    )}
                    {isTranscripts &&
                      replyData?.showTable &&
                      showTable &&
                      (replyData?.relevantFiles?.length ?? 0) > 0 && (
                        <>
                          <TableTranscripts tableContent={replyData?.relevantFiles || []} />
                        </>
                      )}

                    {sourceType === 'Surveys' && (
                      <div
                        className={`chatreply__resp-box__footer__source-box ${
                          sourceType == 'Surveys' && !collapseHiddenText
                            ? 'chatreply__resp-box__footer__survey-moreitems'
                            : sourceType == 'Surveys' &&
                              replyData?.Sources[0]?.trim() !== 'EY conducted surveys' &&
                              replyData?.Sources[0]?.trim() !== 'Surveys' &&
                              'chatreply__resp-box__footer__survey-lessitems'
                        } ${isLastItem ? 'chatreply__resp-box__footer__source-lastItem' : ''}`}>
                        {visibleTextList?.length > 0 &&
                        replyData.Sources[0]?.trim() !== 'EY conducted surveys' ? (
                          <p>
                            <span
                              className={
                                isSideBarOpen
                                  ? 'chatreply__resp-box__footer__links-box chatreply__resp-box__footer__surveys-links-box slider'
                                  : 'chatreply__resp-box__footer__links-box chatreply__resp-box__footer__surveys-links-box'
                              }>
                              {(collapseHiddenText
                                ? visibleTextList
                                : [...visibleTextList, ...hiddenTextList]
                              ).map((link: string, index) => {
                                return (
                                  <>
                                    {index == 0 ? 'Source: ' : ''}
                                    {
                                      <MotifChip className='link-container surveychip' title={link}>
                                        {link}
                                      </MotifChip>
                                    }
                                  </>
                                );
                              })}
                              {collapseHiddenText &&
                                hiddenTextList &&
                                hiddenTextList?.length > 0 && (
                                  <MotifChip className='link-container surveychip'>
                                    {' '}
                                    <a
                                      href={''}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        setCollapseHiddenText(false);
                                      }}
                                      className={'chatreply__no-links chatreply__survey_no-links'}>
                                      + {hiddenTextList?.length} more
                                    </a>
                                  </MotifChip>
                                )}
                            </span>
                          </p>
                        ) : (
                          ((visibleTextList?.length > 0 &&
                            replyData?.Sources[0]?.trim() === 'EY conducted surveys') ||
                            (replyData?.summary &&
                              replyData?.summary.toLowerCase()?.indexOf('no results found') >
                                -1)) && <p>Source: {getResponseSourceType(sourceType)}</p>
                        )}
                        {}
                      </div>
                    )}
                    {visibleLinks?.length > 0 && (
                      <div
                        className={
                          isSideBarOpen
                            ? hasSourceLink
                              ? 'chatreply__resp-box__footer__links-box slider'
                              : 'chatreply__no-links-gap'
                            : hasSourceLink
                              ? 'chatreply__resp-box__footer__links-box'
                              : 'chatreply__no-links-gap'
                        }>
                        {(collapseHiddenLinks
                          ? visibleLinks
                          : [...visibleLinks, ...hiddenLinks]
                        ).map((link: string, index) => {
                          const linkHref = link.includes('://')
                            ? link.split('://')[1]
                            : link.split('www.')[1];
                          return linkHref ? (
                            // <div className='link-container'> removing this tag as we want to hide the source using hidden. This is done on the basis of new requirement in structured project.
                            <div className='hideEmbSources'>
                              <a
                                className='chatreply__resp-box__footer__links-box__source-link'
                                target='_blank'
                                key={`sources-link-${index}`}
                                href={`http://${linkHref}`}>
                                {index + 1}.{formatLink(link)}
                              </a>
                            </div>
                          ) : (
                            // Adding this div to hide the source.This is done on the basis of new requirement in structured project.
                            <div className='hideEmbSources'>
                              <span
                                className={
                                  hasSourceLink
                                    ? 'chatreply__resp-box__footer__links-box__source-link'
                                    : 'chatreply__no-links'
                                }
                                key={`sources-link-${index}`}>
                                {formatLink(link)}
                              </span>
                            </div>
                          );
                        })}
                        {collapseHiddenLinks && hiddenLinks && hiddenLinks?.length > 0 && (
                          // Adding this div to hide the source.This is done on the basis of new requirement in structured project.
                          <div className='hideEmbSources'>
                            <a
                              href={''}
                              onClick={(e) => {
                                e.preventDefault();
                                setCollapseHiddenLinks(false);
                              }}
                              className={
                                hasSourceLink
                                  ? 'chatreply__resp-box__footer__links-box__source-link'
                                  : 'chatreply__no-links'
                              }>
                              + {hiddenLinks?.length} more
                            </a>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <div className='regenerate-container'>
                    {!replyData?.isChunkLoading && (
                      <>
                      <div
                        title={
                          showCopyFailure
                            ? 'Failed to copy response to clipboard'
                            : showCopyText
                              ? 'Copied to clipboard'
                              : 'Copy response'
                        }
                        data-tooltip="Copy"
                        className='chatreply__copy'
                        onClick={handleCopyChat}>
                        {(!showCopyText || showCopyFailure) && (
                          <img src={deepResearchCopyIcon} alt='copy icon' />
                        )}
                        {showCopyText && <img src={deepResearchSuccessIcon} alt='copied icon' />}
                      </div>
                      {replyData?.sqlQuery && (
                       <div
                        title='Export to Excel'
                        className='chatreply__regenerate'
                        data-tooltip="Export to Excel"
                        onClick={handleExportToExcel}>
                        <img src={deepResearchDownload} />
                      </div>
                      )}
                      </>
                  )}
                  {isLastItem && messageLoading === false && (
                      <div
                        title='Regenerate'
                        data-tooltip="Regenerate"
                        className='chatreply__regenerate'
                        onClick={() => handleRegenerate(null, message || '')}>
                        <img src={deepResearchRegenerateIcon} />
                      </div>
                  )}
                
                </div>
              </Fragment>
            )}
            {isMarketReports && !isTranscripts &&
              replyData?.showTable &&
              showTable &&
              (replyData?.relevantFiles?.length ?? 0) > 0 && (
                <>
                  <TableMarketReport tableContent={replyData?.relevantFiles || []} />
                </>
              )}
          </div>
        )}
        {loader && !chunkLoading && (
          <div className={'chatreply__loader-container'}>
            <LoadingIndicator
              color='#2E2E38'
              parentWidth='13px'
              zeroPercentheight='38px'
              fifltyPercentheight='16px'
            />
          </div>
        )}
      </div>
      {isLastItem &&
        !messageLoading &&
        checkedCheckboxes?.length > 0 &&
        ((replyData?.suggestions?.followup?.length ?? 0) > 0 ||
          (replyData?.suggestions?.download?.length ?? 0) > 0) && (
          <div className='chatreply__suggestions_container'>
            <div className='suggestion-img-container'>
              <img src={suggestionIcon} alt='suggestionIcon-alt' />
              <span className='suggestions-text'>Suggestions</span>
            </div>
            <div className='chip-container'>
              {(replyData?.suggestions?.followup || []).filter(Boolean).map(
                (question: string, index: number) =>
                  question.length > 0 && (
                    <MotifChip
                      key={`followup-${index}`}
                      className='chatreply__suggestions'
                      onClick={() => handleSendMessage?.(null, question)}>
                      {question}
                    </MotifChip>
                  )
              )}

              {(replyData?.suggestions?.portfolio || []).filter(Boolean).map(
                (company: string, index: number) =>
                  company.length > 0 && (
                    <MotifChip
                      key={`portfolio-${index}`}
                      className='chatreply__suggestions'
                      onClick={() => {
                        const queryObject = { companyName: encodeURIComponent(company) };
                        const queryValue = JSON.stringify(queryObject);
                        const linkObject = {
                          url: `${process.env.REACT_APP_GENERATE_APP_COMPANYPROFILE_URL}?q=${queryValue}`,
                          name: 'Company Profile'
                        };
                        openLinkInSameTab(linkObject, '');
                      }}>
                      {companyProfileDownloadSuggestion}
                    </MotifChip>
                  )
              )}

              {(replyData?.suggestions?.sector || []).filter(Boolean).map(
                (sector: string, index: number) =>
                  sector.length > 0 && (
                    <MotifChip
                      key={`sector-${index}`}
                      className='chatreply__suggestions'
                      onClick={() => {
                        const linkObject = {
                          url: process.env.REACT_APP_GENERATE_APP_SECTORTREND_URL,
                          name: 'Sector Trend'
                        };
                        openLinkInSameTab(linkObject, '');
                      }}>
                      {sectorTrendDownloadSuggestion}
                    </MotifChip>
                  )
              )}

              {replyData?.sqlQuery &&
                (replyData?.suggestions?.download || []).filter(Boolean).map(
                  (question: string, index: number) =>
                    question.length > 0 && (
                      <MotifChip
                        key={`download-${index}`}
                        className='chip'
                        onClick={async () => {
                          setCsvDownloadError(false);
                          setShowUploadInitiated({ action: 'excel', flag: true });
                          await exportToCSV(
                            replyData?.sqlQuery,
                            RESPONSE_BY.EMBROYNIC,
                            userData,
                            checkedCheckboxes,
                            eyIPInterRadio,
                            validURLinputValue
                          );
                          setShowUploadInitiated({ action: '', flag: false });
                        }}>
                        {question}
                      </MotifChip>
                    )
                )}
            </div>
          </div>
        )}
      {/* {isLastItem && messageLoading === false && (
        <div
          className={`chatreply__suggestions_container ${
            showDnfSuggestion ? 'handleDnfvisible' : 'handleDnfHidden'
          }`}>
          <div className='suggestion-img-container'>
            <img src={suggestionIcon} />
            <span className='suggestions-text'>Suggestions</span>
          </div>
          <div className='chip-container'>
            <MotifChip
              key='open_ai_question'
              className='chatreply__suggestions'
              onClick={() => openDnfQuestionInNewChatHistory()}>
              Would you like to search for this question using Web Search?
            </MotifChip>
          </div>
        </div>
      )} */}
      <ConfirmationSourceModal
        show={showDnfSuggestion && isLastItem && !messageLoading}
        handleContinueClick={() => openDnfQuestionInNewChatHistory()}
        handleNoClick={() => setShowDnfSuggestionState(false)}
      />
      {showUploadInitiated.flag && (
        <MotifToast
          className={!csvDownloadError ? 'toast-container-public' : 'toast-container-public-error'}
          variant={!csvDownloadError ? 'info' : 'error'}
          actionName=''
          position='bottom'
          onClose={() => {
            setShowUploadInitiated({ action: '', flag: false });
          }}>
          {showUploadInitiated.action !== 'ppt' ? (
            <div>
              {!csvDownloadError
                ? 'Downloading Excel'
                : 'Download excel is not supported in history chats, try asking the question again! '}
            </div>
          ) : (
            <div>Downloading excel</div>
          )}
        </MotifToast>
      )}
    </Fragment>
  );
};

export default React.memo(GetChatResponse);
